import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { useCurrency } from '../../hooks/useCurrency';
import { fallbackParts } from '../../data/fallbackData';
import api from '../../api';
import './PartsCatalog.css';

const MODEL_ORDER = ['land200', 'prado150', 'lexus-rx', 'rav4', 'crown', 'prius30', 'hs250'];

const initModels = MODEL_ORDER.filter(m => fallbackParts.some(p => p.carModel === m));

const PartsCatalog = ({ isOpen: controlledOpen, onOpenChange } = {}) => {
  const { t, lang } = useTranslation();
  const { formatPrice } = useCurrency();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

  const setIsOpen = (next) => {
    if (isControlled) {
      onOpenChange?.(typeof next === 'function' ? next(controlledOpen) : next);
    } else {
      setUncontrolledOpen(typeof next === 'function' ? next(uncontrolledOpen) : next);
    }
  };
  const [parts, setParts] = useState(fallbackParts);
  const [activeModel, setActiveModel] = useState(initModels[0] || '');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!isOpen || loaded) return;
    api.get('/api/parts')
      .then(res => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          setParts(res.data);
          const models = [...new Set(res.data.map(p => p.carModel))];
          const sorted = MODEL_ORDER.filter(m => models.includes(m));
          if (sorted.length > 0) setActiveModel(sorted[0]);
          setLoaded(true);
        }
      })
      .catch(() => { setLoaded(true); });
  }, [isOpen, loaded]);

  const models = MODEL_ORDER.filter(m => parts.some(p => p.carModel === m));
  const currentParts = parts.filter(p => p.carModel === activeModel);
  const oilParts = currentParts.filter(p => p.category === 'oils');
  const suspParts = currentParts.filter(p => p.category === 'parts');
  const modelTitle = currentParts[0]?.carModelTitle || activeModel;
  const showRateDisclaimer = lang === 'ru' || lang === 'en';

  const partName = (item) => {
    if (lang === 'mn') return item.nameMn;
    if (lang === 'en') return item.nameEn;
    return item.nameRu || item.nameMn;
  };

  return (
    <section className="parts-catalog-section">
      <div className="container">
      <div className="parts-catalog">
      <button
        className={`parts-catalog__toggle ${isOpen ? 'parts-catalog__toggle--open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{t('parts.toggleBtn')}</span>
        <svg
          className={`parts-catalog__chevron ${isOpen ? 'parts-catalog__chevron--open' : ''}`}
          viewBox="0 0 24 24" width="20" height="20"
        >
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" fill="currentColor"/>
        </svg>
      </button>

      {isOpen && (
        <div className="parts-catalog__content">
          <div className="parts-catalog__tabs">
            {models.map(model => {
              const title = parts.find(p => p.carModel === model)?.carModelTitle || model;
              return (
                <button
                  key={model}
                  className={`parts-catalog__tab ${activeModel === model ? 'parts-catalog__tab--active' : ''}`}
                  onClick={() => setActiveModel(model)}
                >
                  {title}
                </button>
              );
            })}
          </div>

          {currentParts.length > 0 && (
            <div className="parts-catalog__model">
              <h4 className="parts-catalog__model-title">{modelTitle}</h4>

              {oilParts.length > 0 && (
                <>
                  <div className="parts-catalog__section-label">{t('parts.oilsLabel')}</div>
                  <div className="parts-catalog__table">
                    <div className="parts-catalog__row parts-catalog__row--header">
                      <span>{t('parts.headerName')}</span>
                      <span>{t('parts.headerQty')}</span>
                      <span>{t('parts.headerBrand')}</span>
                      <span>{t('parts.headerPrice')}</span>
                    </div>
                    {oilParts.map(item => (
                      <div key={item.id} className="parts-catalog__row">
                        <span>{partName(item)}</span>
                        <span>{item.quantity}</span>
                        <span>{item.brand}</span>
                        <span className="parts-catalog__price">
                          {item.priceMnt > 0 ? formatPrice(item.priceMnt, { showFrom: false }) : '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {suspParts.length > 0 && (
                <>
                  <div className="parts-catalog__section-label">{t('parts.partsLabel')}</div>
                  <div className="parts-catalog__table">
                    <div className="parts-catalog__row parts-catalog__row--header">
                      <span>{t('parts.headerName')}</span>
                      <span>{t('parts.headerQty')}</span>
                      <span>{t('parts.headerBrand')}</span>
                      <span>{t('parts.headerPrice')}</span>
                    </div>
                    {suspParts.map(item => (
                      <div key={item.id} className="parts-catalog__row">
                        <span>{partName(item)}</span>
                        <span>{item.quantity}</span>
                        <span>{item.brand}</span>
                        <span className="parts-catalog__price">
                          {item.priceMnt > 0 ? formatPrice(item.priceMnt, { showFrom: false }) : '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="parts-catalog__disclaimer">
                {t('parts.disclaimer')}
                {showRateDisclaimer && (
                  <p className="parts-catalog__rate-note">{t('parts.rateDisclaimer')}</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
    </div>
    </section>
  );
};

export default PartsCatalog;